import * as React from 'react';
import { connect } from 'react-redux';
import { uniqBy } from 'lodash';

import MessengerContact from './MessengerContact';
import MessengerFilterByType from './filter/MessengerFilterByType';
import MessengerFilterByState from './filter/by-state/MessengerFilterByState';
import MessengerConversationsSearch from './MessengerConversationsSearch';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import Spinner from 'components/spinner/Spinner';

import { fetchConversations } from '../reducers/conversations';

import { t } from 'utils/language';
import { shouldMessengerBeDisabled } from 'utils/messages';

import useInfiniteScroll from 'hooks/use-infinite-scroll';
import useDidMountEffect from 'hooks/use-didMount';

import type { CampaignOverview, MessengerConversation, MessengerNotifications } from 'types';
import { ConversationCount } from '../types/ConversationsState.type';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: true
});

type StateProps = {
  conversations: Array<MessengerConversation>,
  messengerNotifications: MessengerNotifications,
  overview: CampaignOverview,
  isLoading: boolean,
  hasMoreConversations: boolean,
  selectedConversation: MessengerConversation,
  conversationCountByType: ConversationCount,
  isConversationCountLoading: boolean
};

type DispatchProps = {
  dispatchFetchConversations: (
    campaignId: number,
    page: number,
    params: { filterByType: string, filterByState: string, searchQuery: string | undefined | null },
    isCountRequestRequired?: boolean
  ) => Promise<void>
}

type OwnProps = {
  subcampaignId: number | undefined | null,
  onSelectConversation: (a: MessengerConversation) => void,
  onMarkConversationAsUnread: (a: MessengerConversation) => void,
  campaignId: number
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  searchQuery: string | undefined | null,
  filterByType: string,
  filterByState: string,
  isFetching: boolean,
  page: number
};

const MessengerConversations = ({
  conversations,
  messengerNotifications,
  subcampaignId,
  campaignId,
  isLoading,
  isConversationCountLoading,
  hasMoreConversations,
  selectedConversation,
  conversationCountByType,
  onSelectConversation,
  onMarkConversationAsUnread,
  overview,
  dispatchFetchConversations
}: Props) => {
  const [state, setState] = React.useState<State>({
    searchQuery: null,
    filterByType: 'all',
    filterByState: 'all',
    isFetching: false,
    page: 1
  });

  const { searchQuery, filterByType, filterByState, isFetching, page } = state;

  useDidMountEffect(() => {
    dispatchFetchConversations(campaignId, page, { filterByType, filterByState, searchQuery }, true);
  }, [filterByType]);

  useDidMountEffect(() => {
    dispatchFetchConversations(campaignId, page, { filterByType, filterByState, searchQuery });
  }, [filterByState, searchQuery]);

  const handleFilterChange = (value: string, filterType: 'filterByType' | 'filterByState' | 'searchQuery') => {
    setState((prevState) => ({
      ...prevState,
      [filterType]: value,
      page: 1
    }));
  };

  const filterConversation = (): boolean => {
    const unreadChatsMap = messengerNotifications.unreadChatsMap || {};

    if (!selectedConversation) {
      return false;
    }

    if (filterByType === 'unread' && !unreadChatsMap[selectedConversation.externalId]) {
      return false;
    }

    if (filterByState !== 'all') {
      const conversationExternalIdList = overview[`${filterByState}Overview`].conversationExternalIdList;

      if (!conversationExternalIdList.includes(selectedConversation.externalId)) {
        return false;
      }
    }

    return !conversations.some(conversation => conversation.subcampaignId === selectedConversation.subcampaignId);
  };

  const loadMoreItems = () => {
    setState((prevState) => ({
      ...prevState,
      isFetching: true
    }));

    dispatchFetchConversations(campaignId, page + 1, { filterByType, filterByState, searchQuery })
      .then(() => {
        setState((prevState) => ({
          ...prevState,
          isFetching: false,
          page: prevState.page + 1
        }));
      });
  };

  const renderContextMenu = () => {
    const items = [
      {
        type: 'bulk_message',
        name: 'bulk_message',
        text: t('context_menu.bulk_message_label'),
        meta: {
          campaignId
        }
      }
    ];

    return <MoreButtonWithMenu width={20} contextMenuProps={{ items }} />;
  };

  const searchQueryLowerCase = (searchQuery || '').trim().toLowerCase();
  const conversationCountByState = conversationCountByType[filterByType === 'all' ? 'overall' : 'unread'];

  const creatorsCountByState = {
    accepted: conversationCountByState.agreed,
    pending: conversationCountByState.pending,
    revoked: conversationCountByState.revoked
  };

  const [lastElementRef] = useInfiniteScroll(
    hasMoreConversations ? loadMoreItems : () => {},
    isFetching
  );

  const showSelectedConversationExplicitly = filterConversation();
  const allConversations = (showSelectedConversationExplicitly ? [selectedConversation, ...conversations] : conversations);
  const uniqConversation = uniqBy(allConversations, 'externalId');

  return (
    <section
      className="messenger-conversations"
      data-qa="messenger-conversations"
    >
      <div className="messenger-conversations__header">
        <span className="messenger-conversations__header-title">
          {t('messenger.creators_label')}
        </span>
        <MessengerFilterByType
          filterByType={filterByType}
          onClick={(value) => handleFilterChange(value, 'filterByType')}
        />
        <MessengerFilterByState
          filterByState={filterByState}
          creatorsCountByState={creatorsCountByState}
          onClick={(value) => handleFilterChange(value, 'filterByState')}
          isConversationCountLoading={isConversationCountLoading}
        />
        <div className="messenger-conversations__header-context">
          {renderContextMenu()}
        </div>
      </div>
      <ul className="messenger-conversations__conversations">
        <MessengerConversationsSearch
          query={searchQuery}
          onChange={(value) => handleFilterChange(value, 'searchQuery')}
        />
        {
          isLoading ? (
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          ) : uniqConversation.map((conversation, index) => {
            const disableMessenger = conversation ? shouldMessengerBeDisabled(conversation.subcampaign) : false;

            if (conversations.length === index + 1 && hasMoreConversations) {
              return (
                <MessengerContact
                  key={conversation.externalId}
                  searchQuery={searchQueryLowerCase}
                  profile={conversation.subcampaign.creatorProfile}
                  lastMessage={conversation.lastMessage}
                  hasNotification={messengerNotifications.unreadChatsMap && Boolean(messengerNotifications.unreadChatsMap[conversation.externalId])}
                  selected={subcampaignId ? subcampaignId === conversation.subcampaign.id : false}
                  lightweightSelected={subcampaignId ? disableMessenger && subcampaignId === conversation.subcampaign.id : false}
                  conversation={conversation}
                  data={{ qa: 'conversations-contact__artist-name' }}
                  onSelectConversation={onSelectConversation}
                  onMarkConversationAsUnread={onMarkConversationAsUnread}
                  forwardedRef={lastElementRef}
                  isFetching={isFetching}
                />
              );
            }
            return (
              <MessengerContact
                key={conversation.externalId}
                searchQuery={searchQueryLowerCase}
                profile={conversation.subcampaign.creatorProfile}
                lastMessage={conversation.lastMessage}
                hasNotification={messengerNotifications.unreadChatsMap && Boolean(messengerNotifications.unreadChatsMap[conversation.externalId])}
                selected={subcampaignId ? subcampaignId === conversation.subcampaign.id : false}
                lightweightSelected={subcampaignId ? disableMessenger && subcampaignId === conversation.subcampaign.id : false}
                conversation={conversation}
                data={{ qa: 'conversations-contact__artist-name' }}
                onSelectConversation={onSelectConversation}
                onMarkConversationAsUnread={onMarkConversationAsUnread}
              />
            );
          })
        }
        {
          isFetching && !isLoading && (
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          )
        }
      </ul>
    </section>
  );
};

const mapStateToProps = ({ conversations, messengerNotifications, campaignOverview }): StateProps => ({
  conversations: conversations.list,
  messengerNotifications,
  overview: campaignOverview.overview,
  isLoading: conversations.isLoading,
  hasMoreConversations: conversations.hasMoreConversations,
  selectedConversation: conversations.selectedConversation,
  conversationCountByType: conversations.count,
  isConversationCountLoading: conversations.isConversationCountLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchConversations: (
    campaignId: number,
    page: number,
    params: { filterByType: string, filterByState: string, searchQuery: string | undefined | null },
    isCountRequestRequired?: boolean) => dispatch(fetchConversations(campaignId, page, params, isCountRequestRequired))
});

export default connect(mapStateToProps, mapDispatchToProps)(MessengerConversations);
